import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Layout } from '@components/Layout';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { Navigate } from 'react-router-dom';

// Import providers statically - they must be available synchronously
import { BacklogProvider, DevPriorityProvider } from '../features/backlog-management';
import { EolEosProvider } from '../features/eol-eos';

// Lazy load page components
const HomePage = lazy(() => import('../pages/HomePage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('../pages/UnauthorizedPage'));

// Lazy load feature modules
const AbsenceRequestPage = lazy(() => import('../features/AbsenceRequest').then(m => ({ default: m.AbsenceRequestPage })));
const EmployeeForms = lazy(() => import('../features/employee-forms').then(m => ({ default: m.EmployeeForms })));
const OnCallManager = lazy(() => import('../features/oncall-manager/OnCallManager'));
const InventoryPage = lazy(() => import('../pages/Inventory').then(m => ({ default: m.InventoryPage })));
const AssetDetailPage = lazy(() => import('../pages/Inventory').then(m => ({ default: m.AssetDetailPage })));
const TeamsLandingPage = lazy(() => import('../features/oncall-manager/components/TeamsLandingPage'));
const UsacImportPage = lazy(() => import('../features/usac-import').then(m => ({ default: m.UsacImportPage })));
const ServiceTimeAnalysisPage = lazy(() => import('../features/service-time-analysis').then(m => ({ default: m.ServiceTimeAnalysisPage })));
const DevelopmentDashboardPage = lazy(() => import('../features/development-dashboard').then(m => ({ default: m.DevelopmentDashboardPage })));
const SalesDashboardPage = lazy(() => import('../features/sales-dashboard/pages/SalesDashboardPage'));
const RedGreenPage = lazy(() => import('../features/red-green/pages/RedGreenPage'));
const CwToolsPage = lazy(() => import('../features/cw-tools/CwToolsPage').then(m => ({ default: m.CwToolsPage })));
const HpeBomPage = lazy(() => import('../features/hpe-bom').then(m => ({ default: m.HpeBomPage })));
const ReportDocumentationPage = lazy(() => import('../features/reports-documentation').then(m => ({ default: m.ReportDocumentationPage })));
const ReportSurveyPage = lazy(() => import('../features/reports-documentation').then(m => ({ default: m.ReportSurveyPage })));
const SurveyAdminPage = lazy(() => import('../features/reports-documentation').then(m => ({ default: m.SurveyAdminPage })));
const ExperianExportPage = lazy(() => import('../features/experian-export').then(m => ({ default: m.ExperianExportPage })));
const OpportunityAnalysisPage = lazy(() => import('../features/opportunity-analysis').then(m => ({ default: m.OpportunityAnalysisPage })));
const SalesHeatMapPage = lazy(() => import('../features/sales-heat-map/SalesHeatMapPage').then(m => ({ default: m.SalesHeatMapPage })));
const ClientInformationPage = lazy(() => import('../features/client-information').then(m => ({ default: m.ClientInformationPage })));
const PlatformMappingPage = lazy(() => import('../features/platform-mapping').then(m => ({ default: m.PlatformMappingPage })));
const CompanyTeamCleanupPage = lazy(() => import('../features/company-team-cleanup').then(m => ({ default: m.CompanyTeamCleanupPage })));
const EmployeeUpdatePage = lazy(() => import('../features/employee-update').then(m => ({ default: m.EmployeeUpdatePage })));
const HpSalesTrackerPage = lazy(() => import('../features/hp-sales-tracker').then(m => ({ default: m.HpSalesTrackerPage })));
const LokiAiPage = lazy(() => import('../features/loki-ai').then(m => ({ default: m.LokiAiPage })));
const NettechChatPage = lazy(() => import('../features/nettech-chat').then(m => ({ default: m.NettechChatPage })));
const PurchaseRequestPage = lazy(() => import('../features/purchase-request').then(m => ({ default: m.PurchaseRequestPage })));
const SecretsListPage = lazy(() => import('../features/key-management').then(m => ({ default: m.SecretsListPage })));
const SecretDetailPage = lazy(() => import('../features/key-management').then(m => ({ default: m.SecretDetailPage })));
const DeletedSecretsPage = lazy(() => import('../features/key-management').then(m => ({ default: m.DeletedSecretsPage })));
const PermissionsPage = lazy(() => import('../features/key-management').then(m => ({ default: m.PermissionsPage })));
const NtPassPage = lazy(() => import('../features/ntpass').then(m => ({ default: m.NtPassPage })));
const ChangeControlPage = lazy(() => import('../features/change-control').then(m => ({ default: m.ChangeControlPage })));

// Reports
const ReportsLanding = lazy(() => import('../pages/Reports').then(m => ({ default: m.ReportsLanding })));
const MaintenanceSupportRevenue = lazy(() => import('../pages/Reports').then(m => ({ default: m.MaintenanceSupportRevenue })));

// Backlog management - pages only (providers imported statically above)
const AvailableBacklogsPage = lazy(() => import('../features/backlog-management').then(m => ({ default: m.AvailableBacklogsPage })));
const BacklogListPage = lazy(() => import('../features/backlog-management').then(m => ({ default: m.BacklogListPage })));
const DevPriorityPage = lazy(() => import('../features/backlog-management').then(m => ({ default: m.DevPriorityPage })));

// NT Goal Dashboard
const NtGoalDashboardLayout = lazy(() => import('../features/ntgoal-dashboard').then(m => ({ default: m.NtGoalDashboardLayout })));

// EOL/EOS - page only (provider imported statically above)
const EolEosPage = lazy(() => import('../features/eol-eos').then(m => ({ default: m.EolEosPage })));

// Document Acceptance
const DocumentAcceptancePage = lazy(() => import('../features/document-acceptance').then(m => ({ default: m.DocumentAcceptancePage })));
const AcceptanceManagerPage = lazy(() => import('../features/document-acceptance').then(m => ({ default: m.AcceptanceManagerPage })));

// Estimate Action Plan
const EstimateListPage = lazy(() => import('../features/estimate-action-plan').then(m => ({ default: m.EstimateListPage })));
const TaskEditorPage = lazy(() => import('../features/estimate-action-plan').then(m => ({ default: m.TaskEditorPage })));

/**
 * Application router configuration
 * Uses React Router 6 with createBrowserRouter for better data loading support
 * All routes use lazy loading for better code splitting
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/unauthorized',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <UnauthorizedPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'absence-request',
        element: (
          <ProtectedRoute>
            <AbsenceRequestPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory',
        element: (
          <ProtectedRoute requiredRoles={['inventory.readonly', 'inventory.manager', 'inventory.admin', 'NtForms.Developer']}>
            <InventoryPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/asset/:id',
        element: (
          <ProtectedRoute requiredRoles={['inventory.readonly', 'inventory.manager', 'inventory.admin', 'NtForms.Developer']}>
            <AssetDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'employee-forms',
        element: (
          <ProtectedRoute requiredRoles={['Submit.EmployeeForm']}>
            <EmployeeForms />
          </ProtectedRoute>
        ),
      },
      {
        path: 'oncall-manager',
        element: (
          <ProtectedRoute>
            <OnCallManager />
          </ProtectedRoute>
        ),
      },
      {
        // Teams landing page for notifications - no auth required (Teams handles it)
        path: 'oncall-alert',
        element: <TeamsLandingPage />,
      },
      {
        path: 'usac-import',
        element: (
          <ProtectedRoute requiredRoles={['USAC.User']}>
            <UsacImportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'backlogs',
        element: (
          <ProtectedRoute>
            <BacklogProvider>
              <AvailableBacklogsPage />
            </BacklogProvider>
          </ProtectedRoute>
        ),
      },
      {
        path: 'backlog/:id',
        element: (
          <ProtectedRoute>
            <BacklogProvider>
              <BacklogListPage />
            </BacklogProvider>
          </ProtectedRoute>
        ),
      },
      {
        path: 'devpriority',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer']}>
            <DevPriorityProvider>
              <DevPriorityPage />
            </DevPriorityProvider>
          </ProtectedRoute>
        ),
      },
      {
        path: 'service-time-analysis',
        element: (
          <ProtectedRoute requiredRoles={['ServiceTimeAnalysis.Viewer', 'NtForms.Developer', 'ServiceDeliveryManager']}>
            <ServiceTimeAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'development-dashboard',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer', 'COO', 'ServiceDeliveryManager']}>
            <DevelopmentDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ntgoal-dashboard',
        element: (
          <ProtectedRoute requiredRoles={['NettechGoals.Viewer', 'NtForms.Developer']}>
            <NtGoalDashboardLayout />
          </ProtectedRoute>
        ),
      },
      {
        path: 'sales-dashboard',
        element: (
          <ProtectedRoute>
            <SalesDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'red-green',
        element: (
          <ProtectedRoute>
            <RedGreenPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'eol-eos',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer', 'EolEos.Read', 'EolEos.Write', 'EolEos.Import']}>
            <EolEosProvider>
              <EolEosPage />
            </EolEosProvider>
          </ProtectedRoute>
        ),
      },
      {
        path: 'cwtools',
        element: (
          <ProtectedRoute requiredRoles={['CwTools.View', 'NtForms.Developer']}>
            <CwToolsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'hpe-bom',
        element: (
          <ProtectedRoute requiredRoles={['HpeBomProcessing.View', 'NtForms.Developer']}>
            <HpeBomPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'acceptance/:id',
        element: (
          <ProtectedRoute>
            <DocumentAcceptancePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'acceptancemanager',
        element: (
          <ProtectedRoute requiredRoles={['DocumentAcceptance.Manager', 'NtForms.Developer']}>
            <AcceptanceManagerPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'docs',
        element: (
          <ProtectedRoute requiredRoles={['DevDocs.View', 'NtForms.Developer']}>
            <ReportDocumentationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'report-survey',
        element: (
          <ProtectedRoute>
            <ReportSurveyPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'report-survey-admin',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer']}>
            <SurveyAdminPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'experianexport',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.ExperianExporter', 'NtForms.Developer']}>
            <ExperianExportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'opportunityanalysis',
        element: (
          <ProtectedRoute requiredRoles={['OppAnalysis.Reader', 'NtForms.Developer']}>
            <OpportunityAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'salesheatmap',
        element: (
          <ProtectedRoute requiredRoles={['SalesHeatMap.Write', 'SalesHeatMap.Admin', 'NtForms.Developer']}>
            <SalesHeatMapPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'clientinformation',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer']}>
            <ClientInformationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'platform-mapping',
        element: (
          <ProtectedRoute requiredRoles={['AuvikMapper', 'SentinelMapper', 'NtForms.Developer']}>
            <PlatformMappingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'company-team-cleanup',
        element: (
          <ProtectedRoute requiredRoles={['CompanyTeamCleanup', 'NtForms.Developer']}>
            <CompanyTeamCleanupPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'employee-update',
        element: (
          <ProtectedRoute>
            <EmployeeUpdatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'estimate-action-plan',
        element: (
          <ProtectedRoute>
            <EstimateListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'estimate-action-plan/:id',
        element: (
          <ProtectedRoute>
            <TaskEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'hp-sales-tracker',
        element: (
          <ProtectedRoute requiredRoles={['HPSalesTrackerImporter']}>
            <HpSalesTrackerPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'loki',
        element: (
          <ProtectedRoute>
            <LokiAiPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'chat',
        element: (
          <ProtectedRoute>
            <NettechChatPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'purchase-request',
        element: (
          <ProtectedRoute>
            <PurchaseRequestPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'change-control',
        element: (
          <ProtectedRoute>
            <ChangeControlPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer']} authorizedUpns={['wdearment@net-cloud.com', 'tcorapi@net-cloud.com', 'jbahm@net-cloud.com', 'dkretz@net-cloud.com']}>
            <ReportsLanding />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/maintenance-support-revenue',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer']} authorizedUpns={['wdearment@net-cloud.com', 'tcorapi@net-cloud.com', 'jbahm@net-cloud.com', 'dkretz@net-cloud.com']}>
            <MaintenanceSupportRevenue />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ntpass',
        element: (
          <ProtectedRoute requiredRoles={['NtForms.Developer']}>
            <NtPassPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/key-management/secrets',
        element: (
          <ProtectedRoute requiredRoles={['KeyVault.Admin', 'NtForms.Developer']}>
            <SecretsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/key-management/secrets/:name',
        element: (
          <ProtectedRoute requiredRoles={['KeyVault.Admin', 'NtForms.Developer']}>
            <SecretDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/key-management/deleted',
        element: (
          <ProtectedRoute requiredRoles={['KeyVault.Admin', 'NtForms.Developer']}>
            <DeletedSecretsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/key-management/permissions',
        element: (
          <ProtectedRoute requiredRoles={['KeyVault.Admin', 'NtForms.Developer']}>
            <PermissionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/key-management/permissions/:principalId',
        element: (
          <ProtectedRoute requiredRoles={['KeyVault.Admin', 'NtForms.Developer']}>
            <PermissionsPage />
          </ProtectedRoute>
        ),
      },
      // Legacy route redirects for backward compatibility
      {
        path: 'absencerequest',
        element: <Navigate to="/absence-request" replace />,
      },
      {
        path: 'auvikMapping',
        element: <Navigate to="/platform-mapping?platform=auvik" replace />,
      },
      {
        path: 'DattoMapping',
        element: <Navigate to="/platform-mapping?platform=datto" replace />,
      },
      {
        path: 'dattoMapping',
        element: <Navigate to="/platform-mapping?platform=datto" replace />,
      },
      {
        path: 'sentinelmapping',
        element: <Navigate to="/platform-mapping?platform=sentinel" replace />,
      },
      {
        path: 'hpebom',
        element: <Navigate to="/hpe-bom" replace />,
      },
      {
        path: 'acceptance/:id',
        element: <Navigate to="/acceptance/:id" replace />,
      },
      {
        path: 'employeeActions',
        element: <Navigate to="/employee-forms" replace />,
      },
      {
        path: 'employeeActions/Add',
        element: <Navigate to="/employee-forms" replace />,
      },
      {
        path: 'employeeActions/Term',
        element: <Navigate to="/employee-forms" replace />,
      },
      {
        path: 'companyTeamCleanup',
        element: <Navigate to="/company-team-cleanup" replace />,
      },
      {
        path: 'ClientMasterHeatMap',
        element: <Navigate to="/salesheatmap" replace />,
      },
      {
        path: 'HpSalesImporter',
        element: <Navigate to="/hp-sales-tracker" replace />,
      },
      {
        path: 'ProjectActionPlan',
        element: <Navigate to="/estimate-action-plan" replace />,
      },
      {
        path: 'ProjectActionPlan/:id',
        element: <Navigate to="/estimate-action-plan/:id" replace />,
      },
      {
        path: 'DevPriority',
        element: <Navigate to="/devpriority" replace />,
      },
      {
        path: 'RedLightGreenLight',
        element: <Navigate to="/red-green" replace />,
      },
      {
        path: 'usac',
        element: <Navigate to="/usac-import" replace />,
      },
      {
        path: 'ClientFinance',
        element: <Navigate to="/clientinformation" replace />,
      },
      {
        path: 'eoleos',
        element: <Navigate to="/eol-eos" replace />,
      },
      {
        path: 'oncall',
        element: <Navigate to="/oncall-manager" replace />,
      },
      {
        path: 'goaldashboard',
        element: <Navigate to="/ntgoal-dashboard" replace />,
      },
      {
        path: 'salesdashboard',
        element: <Navigate to="/sales-dashboard" replace />,
      },
      {
        path: 'employeeupdate',
        element: <Navigate to="/employee-update" replace />,
      },
      {
        path: 'purchaserequest',
        element: <Navigate to="/purchase-request" replace />,
      },
      {
        path: 'changecontrol',
        element: <Navigate to="/change-control" replace />,
      },
      {
        path: 'egg',
        element: (
          <ProtectedRoute>
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '48px' }}>
              <span>↑ ↑ ↓ ↓ ← → ← → b a start</span>
            </div>
          </ProtectedRoute>
        ),
      },
      // Future routes will be added here as features are migrated
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

export default router;
